// v5.79 — Creative Edit state + logic. The editor renders IN PLACE on the
// canvas tile (CreativeEditNodeOverlay inside ImageNode) with controls in the
// right slide-out SidebarPanel (CreativeEditPanel). PREPARES ARE CONCURRENT
// (Dan: "I should be able to do multiple at a time to process them into
// text"): each tile you hit Edit on keeps its own prepare running with a
// spinner on the tile; only ONE tile at a time is the ACTIVE editor.
import { create } from 'zustand';
import type Konva from 'konva';
import { loadCreativeFontFaces, creativeFontFamily, creativeFontWeight, type CreativeFontEvidence } from './creative-edit-fonts';

// v5.91 — class-organized font library (Dan: "is 25 fonts enough? what do
// we need to cover 100%?"). Ad typography clusters into classes; the OCR
// names the CLASS, the pixel-matcher tests that class's shortlist at
// weights up to 900. The old flat-25 library had ZERO monospace faces and
// nothing loaded above weight 700 — typewriter ads and 900-black headlines
// were unmatchable by construction.
export const FONT_CLASSES: Record<string, string[]> = {
  monospace: ['Space Mono', 'IBM Plex Mono', 'Courier Prime', 'Roboto Mono', 'JetBrains Mono', 'Special Elite'],
  grotesque: ['Inter', 'Roboto', 'Arimo', 'Work Sans', 'DM Sans', 'Manrope', 'Archivo', 'Figtree'],
  geometric: ['Montserrat', 'Poppins', 'Jost', 'Outfit', 'Urbanist', 'Raleway'],
  humanist: ['Open Sans', 'Lato', 'Nunito Sans', 'Source Sans 3', 'PT Sans'],
  condensed: ['Barlow Condensed', 'Oswald', 'Anton', 'Bebas Neue', 'Archivo Narrow', 'Fjalla One', 'Teko'],
  'black-display': ['Archivo Black', 'Alfa Slab One', 'Passion One', 'Titan One', 'Lilita One', 'Luckiest Guy', 'Bangers', 'Righteous'],
  slab: ['Roboto Slab', 'Zilla Slab', 'Alfa Slab One', 'Josefin Slab'],
  'serif-display': ['Playfair Display', 'DM Serif Display', 'Abril Fatface', 'Libre Caslon Text', 'Cormorant Garamond'],
  'serif-text': ['Merriweather', 'Lora', 'PT Serif', 'Source Serif 4', 'Libre Baskerville'],
  rounded: ['Nunito', 'Quicksand', 'Baloo 2', 'Fredoka', 'Comfortaa'],
  script: ['Lobster', 'Pacifico', 'Dancing Script', 'Satisfy'],
  handwritten: ['Caveat', 'Permanent Marker', 'Kalam', 'Shadows Into Light'],
};
export const EDITOR_FONTS: string[] = Array.from(new Set(Object.values(FONT_CLASSES).flat()));
export const classOfFont = (f: string): string =>
  Object.entries(FONT_CLASSES).find(([, fams]) => fams.includes(f))?.[0] ?? 'grotesque';

export interface EditTextNode {
  id: string;
  text: string;
  x: number; y: number; w: number; h: number;
  fontFamily: string;
  fontSize: number;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  /** Solid highlighter band behind each text line (yellow marker style). */
  bgFill?: string;
  /** v5.90 lazy strip: absent/false = original pixels still show this text
   *  (100% identical); true = region erased, editable layer renders. */
  struck?: boolean;
  fontStyle: string;
  align: 'left' | 'center' | 'right';
  lineHeight: number;
  rotation?: number;
  /** px tracking applied so the matched font hits the original ink width. */
  letterSpacing?: number;
  /** OCR-detected typography class — the matcher's candidate pool. */
  fontClass?: string;
}

export interface EditDoc extends CreativeFontEvidence {
  plateUrl: string;
  width: number;
  height: number;
  texts: EditTextNode[];
  /** One-time render-and-fit completed (v5.76) — persisted so user tweaks
   *  are never re-clobbered on a later open. */
  fitted?: boolean;
}

export interface PrepareState {
  runId: number;
  sourceUrl: string;
  /** Source tile's display dims, captured at open() — the saved copy takes
   *  the same visual size/ratio beside the original. */
  tileW?: number;
  tileH?: number;
  docId: number | null;
  phase: 'preparing' | 'ready' | 'failed';
  phaseNote: string;
  error: string | null;
  doc: EditDoc | null;
}

// ---- webfont loading --------------------------------------------------------
const fontStylesheets = new Map<string, Promise<void>>();
export function ensureGoogleFont(family: string): void {
  if (fontStylesheets.has(family)) return;
  const ready = new Promise<void>(resolve => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    const timer = setTimeout(resolve, 6000);
    const done = () => { clearTimeout(timer); resolve(); };
    link.onload = done;
    link.onerror = done;
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:ital,wght@0,400;0,700;0,900;1,400;1,700&display=swap`;
    document.head.appendChild(link);
  });
  fontStylesheets.set(family, ready);
}

async function loadFamilies(families: string[]): Promise<Set<string>> {
  families.forEach(ensureGoogleFont);
  const available = new Set<string>();
  await Promise.all(families.map(async family => {
    await fontStylesheets.get(family);
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<FontFace[][]>(resolve => { timer = setTimeout(() => resolve([]), 6000); });
    try {
      const faces = await Promise.race([
        Promise.all(['400', '700', '900'].map(weight => document.fonts.load(`${weight} 100px ${JSON.stringify(family)}`))),
        timeout,
      ]);
      // check() also returns true when the family does not exist. Only an
      // actual loaded FontFace proves these measurements use this family.
      if (faces.flat().some(face => face.status === 'loaded')) available.add(family);
    } catch { /* unavailable families do not enter pixel matching */ }
    finally { clearTimeout(timer!); }
  }));
  return available;
}

// ---- render-and-fit (v5.76) -------------------------------------------------
// Ink-vs-em law: Gemini boxes measure glyph INK; CSS font-size includes
// ascender/descender padding (caps ink ≈ 0.72em) → height-derived sizes run
// ~40% small. Width-fit leads; height is only a loose cap. Gemini also picks
// condensed faces (Bebas) for wide heavy grotesques — measure candidates in
// the same style class and swap when the pick misses the box width by >12%.
// v5.84 — faces with NO lowercase glyphs: rendering lowercase text in them
// silently uppercases it. Banned for any block containing lowercase letters.
const CAPS_ONLY = ['Bebas Neue', 'Luckiest Guy', 'Bangers'];
// Candidate pool: the block's OCR class shortlist. Heavy display headlines
// also get the black-display + condensed neighbors (the classes visually
// bleed into each other on ads).
const poolFor = (t: { fontClass?: string; fontFamily: string }): string[] => {
  const cls = t.fontClass && FONT_CLASSES[t.fontClass] ? t.fontClass : classOfFont(t.fontFamily);
  const base = FONT_CLASSES[cls] ?? FONT_CLASSES.grotesque;
  if (cls === 'grotesque' || cls === 'geometric' || cls === 'condensed' || cls === 'black-display' || cls === 'slab' || cls === 'humanist') {
    // The classifier confuses display classes (it called a typewriter
    // headline black-display) — let the pixel IoU decide across the union,
    // including monospace: uniform-width faces are common in ads and
    // unmistakable to the scorer.
    return Array.from(new Set([...base, ...FONT_CLASSES['black-display'], ...FONT_CLASSES.condensed, ...FONT_CLASSES.grotesque.slice(0, 4), ...FONT_CLASSES.monospace, ...FONT_CLASSES.slab]));
  }
  return base;
};
const poolOf = (f: string) => FONT_CLASSES[classOfFont(f)] ?? FONT_CLASSES.grotesque;

async function loadCorsImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const el = new window.Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = src;
  });
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.length === 4 ? '#' + hex.slice(1).split('').map((c) => c + c).join('') : hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Binarized ink mask of a canvas region: pixels near the block's fill color. */
function inkMaskOf(ctx: CanvasRenderingContext2D, W: number, H: number, fill: { r: number; g: number; b: number }): Uint8Array | null {
  try {
    const data = ctx.getImageData(0, 0, W, H).data;
    const mask = new Uint8Array(W * H);
    for (let i = 0; i < W * H; i++) {
      const dr = data[i * 4] - fill.r, dg = data[i * 4 + 1] - fill.g, db = data[i * 4 + 2] - fill.b;
      if (dr * dr + dg * dg + db * db < 10000) mask[i] = 1;
    }
    return mask;
  } catch {
    return null; // tainted canvas (non-CORS source) — refine unavailable
  }
}

/** v5.88 — GROUND-TRUTH refine (Dan: "the font needs to be IDENTICAL"):
 *  crop the ORIGINAL image around the OCR box, find the true ink bounding
 *  box (fixes under-sized boxes → wrong sizes), then pick the font whose
 *  rendered glyphs best overlap the real ink (pixel IoU across the library
 *  — a measurement, not an LLM guess). Returns null when the text can't be
 *  isolated (keep the metric fit). */
function refineBlock(
  t: EditTextNode,
  img: HTMLImageElement,
  docW: number,
  docH: number,
  measure: (line: string, family: string, weight: string, italic: string) => number,
  fontPool = poolFor(t),
  candidateWeights: string[] = [],
  renderFamily: (family: string) => string = family => family,
): EditTextNode | null {
  if (t.rotation) return null;
  const fill = hexToRgb(t.fill);
  if (!fill) return null;
  const lines = t.text.split('\n').filter((l) => l.length > 0);
  if (lines.length === 0) return null;
  const weight = String(creativeFontWeight(t.fontStyle));
  const italic = t.fontStyle.includes('italic') ? 'italic ' : '';

  // 1. expanded crop → true ink bbox in image coordinates
  const mx = t.w * 0.25 + 10, my = t.h * 0.18 + 8;
  const cx0 = Math.max(0, Math.floor(t.x - mx)), cy0 = Math.max(0, Math.floor(t.y - my));
  const cx1 = Math.min(docW, Math.ceil(t.x + t.w + mx)), cy1 = Math.min(docH, Math.ceil(t.y + t.h + my));
  const cw = cx1 - cx0, ch = cy1 - cy0;
  if (cw < 8 || ch < 8) return null;
  const s1 = Math.min(1, 420 / cw);
  const W1 = Math.max(8, Math.round(cw * s1)), H1 = Math.max(8, Math.round(ch * s1));
  const cv1 = document.createElement('canvas');
  cv1.width = W1; cv1.height = H1;
  const c1 = cv1.getContext('2d', { willReadFrequently: true });
  if (!c1) return null;
  c1.drawImage(img, cx0, cy0, cw, ch, 0, 0, W1, H1);
  const m1 = inkMaskOf(c1, W1, H1, fill);
  if (!m1) return null;
  let minX = W1, maxX = -1, minY = H1, maxY = -1, inkCount = 0;
  for (let y = 0; y < H1; y++) for (let x = 0; x < W1; x++) if (m1[y * W1 + x]) {
    inkCount++;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  if (inkCount < W1 * H1 * 0.004 || maxX <= minX || maxY <= minY) return null;
  let nx = cx0 + minX / s1, ny = cy0 + minY / s1;
  let nx1 = cx0 + (maxX + 1) / s1, ny1 = cy0 + (maxY + 1) / s1;
  // Clamp to the claimed box: neighboring SAME-COLOR text (e.g. a white
  // subtitle under a white headline) otherwise merges into the ink bbox and
  // poisons both the size and the font scoring. Width may grow generously
  // (under-wide OCR boxes are real); height only slightly.
  // Vertical clamp only: same-color neighbors stack above/below (subtitle
  // under headline); horizontal ink growth is REAL (OCR boxes chronically
  // under-wide — clamping width starved wide fonts of their true size).
  const vTol = Math.max(t.h * 0.10, 8);
  ny = Math.max(ny, t.y - vTol); ny1 = Math.min(ny1, t.y + t.h + vTol);
  const nw = nx1 - nx, nh = ny1 - ny;
  if (nw < 6 || nh < 6) return null;

  // 2. reference ink mask over the TRUE box at comparison resolution
  const s2 = Math.min(1, 360 / nw);
  const W2 = Math.max(8, Math.round(nw * s2)), H2 = Math.max(8, Math.round(nh * s2));
  const cvO = document.createElement('canvas');
  cvO.width = W2; cvO.height = H2;
  const cO = cvO.getContext('2d', { willReadFrequently: true });
  if (!cO) return null;
  cO.drawImage(img, nx, ny, nw, nh, 0, 0, W2, H2);
  const refMask = inkMaskOf(cO, W2, H2, fill);
  if (!refMask) return null;

  // 3. per candidate font: width-fit size in the TRUE box, render, IoU score
  const hasLower = /[a-z]/.test(t.text);
  const pool = hasLower ? fontPool.filter((f) => !CAPS_ONLY.includes(f)) : fontPool;
  const lineSlot = nh / lines.length;
  const widest = lines.reduce((a, b) => (measure(a, t.fontFamily, weight, italic) >= measure(b, t.fontFamily, weight, italic) ? a : b), lines[0]);
  const cvR = document.createElement('canvas');
  cvR.width = W2; cvR.height = H2;
  const cR = cvR.getContext('2d', { willReadFrequently: true });
  if (!cR) return null;
  let bestFamily = t.fontFamily, bestSize = t.fontSize, bestScore = -1;
  const renderScore = (f: string, size: number, tracking: number, wgt: string = weight): number => {
    cR.setTransform(1, 0, 0, 1, 0, 0);
    cR.fillStyle = '#000';
    cR.fillRect(0, 0, W2, H2);
    cR.fillStyle = '#fff';
    cR.font = `${italic}${wgt} ${size * s2}px "${renderFamily(f)}", sans-serif`;
    for (let i = 0; i < lines.length; i++) {
      const met = cR.measureText(lines[i]);
      const lw = met.width + tracking * s2 * Math.max(0, lines[i].length - 1);
      const lx = t.align === 'center' ? (W2 - lw) / 2 : t.align === 'right' ? W2 - lw : 0;
      const asc = met.actualBoundingBoxAscent || size * s2 * 0.75;
      if (tracking === 0) {
        cR.fillText(lines[i], lx, i * lineSlot * s2 + asc);
      } else {
        let cx = lx;
        for (const ch of lines[i]) {
          cR.fillText(ch, cx, i * lineSlot * s2 + asc);
          cx += cR.measureText(ch).width + tracking * s2;
        }
      }
    }
    let data: Uint8ClampedArray;
    try { data = cR.getImageData(0, 0, W2, H2).data; } catch { return -1; }
    let inter = 0, uni = 0;
    for (let i = 0; i < W2 * H2; i++) {
      const r = data[i * 4] > 128 ? 1 : 0;
      const o = refMask[i];
      if (r && o) inter++;
      if (r || o) uni++;
    }
    return uni > 0 ? inter / uni : 0;
  };
  const heightFit = lineSlot / 0.72; // glyph caps fill the line slot
  // Heavy ad headlines are usually 800-900 weight — 700-only trials made
  // everything too light and let condensed faces win on mass.
  const weights = Array.from(new Set([weight, ...candidateWeights, ...(Number(weight) >= 700 ? ['700', '900'] : ['400', '700'])]));
  let bestWeight = weight;
  for (const f of pool) for (const wgt of weights) {
    const wFit = (nw / Math.max(1, measure(widest, f, wgt, italic))) * 100;
    // Try the size that matches the WIDTH and the size that matches the
    // HEIGHT — with per-size tracking to reconcile the other axis. The
    // single width-locked trial starved wide fonts and let condensed faces
    // win on height alone (the judges' top defect).
    const candidates = Array.from(new Set([wFit, heightFit, (wFit + heightFit) / 2].map((x) => Math.round(x)))).filter((x) => x >= 6);
    for (const size of candidates) {
      // tracking so the widest line spans the true ink width at THIS size
      const natural = measure(widest, f, wgt, italic) * (size / 100);
      const track = Math.max(-size * 0.05, Math.min(size * 0.25, (nw - natural) / Math.max(1, widest.length - 1)));
      const score = renderScore(f, size, track, wgt);
      if (score < 0) return null;
      (window as unknown as { __refineLog?: unknown[] }).__refineLog?.push({ text: t.text.slice(0, 14), font: f, w: wgt, size, track: Math.round(track), score: Math.round(score * 1000) / 1000 });
      if (score > bestScore) { bestScore = score; bestFamily = f; bestSize = size; bestWeight = wgt; }
    }
  }
  if (bestScore < 0.25) return null; // nothing matched the real ink — keep metric fit

  let fontSize = Math.max(8, Math.floor(bestSize));
  // Letters must never collide or overflow: cap squeeze at -5%, and when the
  // tracked line still overshoots the ink width (or the canvas), scale the
  // size down to fit instead.
  const trackFor = (fs: number) => Math.max(-fs * 0.05, Math.min(fs * 0.25, (nw - measure(widest, bestFamily, bestWeight, italic) * (fs / 100)) / Math.max(1, widest.length - 1)));
  for (let guard = 0; guard < 6; guard++) {
    const nat = measure(widest, bestFamily, bestWeight, italic) * (fontSize / 100);
    // Konva applies letterSpacing after EVERY glyph (n, not n-1) — the n-1
    // math left the last letter clipped at the canvas edge.
    const tracked = nat + trackFor(fontSize) * widest.length;
    const maxW = Math.min(nw * 1.04, docW - nx - 8);
    if (tracked <= maxW) break;
    fontSize = Math.max(8, Math.floor(fontSize * (maxW / tracked)));
  }
  const natural = measure(widest, bestFamily, bestWeight, italic) * (fontSize / 100);
  const letterSpacing = Math.round(trackFor(fontSize) * 10) / 10;
  const fontStyle = `${bestWeight === '700' ? 'bold' : bestWeight === '400' ? 'normal' : bestWeight}${italic ? ' italic' : ''}`;
  // Multi-line display blocks keep tight leading even when the size had to
  // shrink for width — recomputing leading from the slot spread the lines
  // far looser than the original's near-touching stack.
  const lineHeight = Math.round(Math.min(lines.length > 1 ? 1.1 : 1.7, Math.max(0.75, lineSlot / fontSize)) * 100) / 100;
  // Konva TRUNCATES glyphs beyond `width` when wrap='none' — the box must
  // cover the full tracked line or the last letter vanishes ("JUST DROPPE").
  const trackedW = natural + letterSpacing * widest.length;
  return {
    ...t,
    fontFamily: bestFamily,
    fontStyle,
    fontSize,
    lineHeight,
    letterSpacing,
    x: Math.round(nx),
    y: Math.round(ny),
    w: Math.min(Math.ceil(Math.max(nw, trackedW)) + 6, docW - Math.round(nx) - 2),
    h: Math.round(nh),
  };
}

async function fitDoc(doc: EditDoc, sourceUrl?: string): Promise<EditDoc> {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return { ...doc, fitted: true };
  await loadFamilies(Array.from(new Set(doc.texts.flatMap((t) => poolFor(t)))));
  const measure = (line: string, family: string, weight: string, italic: string) => {
    ctx.font = `${italic}${weight} 100px "${family}", sans-serif`;
    return Math.max(1, ctx.measureText(line).width);
  };
  // metric fit (fallback when the original's pixels aren't available)
  const metricFit = (t: EditTextNode): EditTextNode => {
    const lines = t.text.split('\n');
    const weight = String(creativeFontWeight(t.fontStyle));
    const italic = t.fontStyle.includes('italic') ? 'italic ' : '';
    const widest = lines.reduce((a, b) =>
      measure(a, t.fontFamily, weight, italic) >= measure(b, t.fontFamily, weight, italic) ? a : b, lines[0] ?? '');
    const naturalSize = (t.h / (lines.length * t.lineHeight)) / 0.72;
    const hasLower = /[a-z]/.test(t.text);
    const pool = hasLower ? poolOf(t.fontFamily).filter((f) => !CAPS_ONLY.includes(f)) : poolOf(t.fontFamily);
    let family = t.fontFamily;
    let err = hasLower && CAPS_ONLY.includes(family)
      ? Number.POSITIVE_INFINITY
      : Math.abs(measure(widest, family, weight, italic) * (naturalSize / 100) - t.w);
    if (err > t.w * 0.08) {
      for (const f of pool) {
        const e = Math.abs(measure(widest, f, weight, italic) * (naturalSize / 100) - t.w);
        if (e < err) { err = e; family = f; }
      }
    }
    const widthFit = (t.w / measure(widest, family, weight, italic)) * 100;
    const heightCap = Math.floor(t.h / (lines.length * 0.8));
    const fontSize = Math.max(8, Math.floor(Math.min(widthFit, naturalSize * 1.25, heightCap)));
    const lineHeight = Math.round(Math.min(1.6, Math.max(0.8, (t.h / lines.length) / fontSize)) * 100) / 100;
    return { ...t, fontFamily: family, fontSize, lineHeight, y: t.y };
  };

  const orig = sourceUrl ? await loadCorsImage(sourceUrl) : null;
  const texts = doc.texts.map((t) => {
    if (orig) {
      try {
        const refined = refineBlock(t, orig, doc.width, doc.height, measure);
        if (refined) return refined;
      } catch { /* fall through to metric fit */ }
    }
    return metricFit(t);
  });
  return { ...doc, fitted: true, texts };
}

/** v5.90 — fit ONE block at conversion time, against the pre-strip plate
 *  (the original pixels of this text). */
async function fitSingleBlock(t: EditTextNode, preStripPlateUrl: string, docW: number, docH: number, evidence: CreativeFontEvidence = {}): Promise<EditTextNode> {
  const custom = await loadCreativeFontFaces(evidence);
  const customNames = new Set((evidence.fontFaces ?? []).map(f => f.family));
  const googleCandidates = (evidence.fontCandidates ?? []).filter(f => !customNames.has(f.family));
  const available = await loadFamilies([...poolFor(t).filter(f => !customNames.has(f)), ...googleCandidates.map(f => f.family)]);
  // Only successfully loaded custom files enter matching. Otherwise their
  // fallback glyphs could win and falsely claim the brand font was matched.
  const fontPool = Array.from(new Set([...Array.from(custom), ...Array.from(available)]));
  if (fontPool.length === 0) throw new Error('Could not load fonts for this text. The original image is unchanged. Please reload and retry.');
  const candidateWeights = [...(evidence.fontCandidates ?? []), ...(evidence.fontFaces ?? [])]
    .flatMap(f => f.weight && /^\d{3}$/.test(f.weight) ? [f.weight] : []);
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return t;
  const measure = (line: string, family: string, weight: string, italic: string) => {
    ctx.font = `${italic}${weight} 100px "${creativeFontFamily(family, evidence)}", sans-serif`;
    return Math.max(1, ctx.measureText(line).width);
  };
  const orig = await loadCorsImage(preStripPlateUrl);
  if (orig) {
    try {
      const refined = refineBlock(t, orig, docW, docH, measure, fontPool, candidateWeights, family => creativeFontFamily(family, evidence));
      if (refined) return refined;
    } catch { /* fall through */ }
  }
  // metric fallback (same math as fitDoc's)
  const lines = t.text.split('\n');
  const weight = String(creativeFontWeight(t.fontStyle));
  const italic = t.fontStyle.includes('italic') ? 'italic ' : '';
  const widest = lines.reduce((a, b) =>
    measure(a, t.fontFamily, weight, italic) >= measure(b, t.fontFamily, weight, italic) ? a : b, lines[0] ?? '');
  const naturalSize = (t.h / (lines.length * t.lineHeight)) / 0.72;
  const hasLower = /[a-z]/.test(t.text);
  const pool = hasLower ? fontPool.filter((f) => !CAPS_ONLY.includes(f)) : fontPool;
  let family = fontPool.includes(t.fontFamily) ? t.fontFamily : fontPool[0];
  let err = hasLower && CAPS_ONLY.includes(family)
    ? Number.POSITIVE_INFINITY
    : Math.abs(measure(widest, family, weight, italic) * (naturalSize / 100) - t.w);
  if (err > t.w * 0.08) {
    for (const f of pool) {
      const e = Math.abs(measure(widest, f, weight, italic) * (naturalSize / 100) - t.w);
      if (e < err) { err = e; family = f; }
    }
  }
  const widthFit = (t.w / measure(widest, family, weight, italic)) * 100;
  const heightCap = Math.floor(t.h / (lines.length * 0.8));
  const fontSize = Math.max(8, Math.floor(Math.min(widthFit, naturalSize * 1.25, heightCap)));
  const lineHeight = Math.round(Math.min(1.6, Math.max(0.8, (t.h / lines.length) / fontSize)) * 100) / 100;
  return { ...t, fontFamily: family, fontSize, lineHeight };
}

function patchDoc(docId: number, doc: EditDoc): void {
  fetch(`/api/creative-edit/docs/${docId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc }),
  }).catch(() => {});
}

// ---- store ------------------------------------------------------------------
interface CreativeEditState {
  /** Per-tile prepare/edit state — every tile the user hit Edit on. */
  prepares: Record<string, PrepareState>;
  /** Tile whose editor (stage + panel controls) is focused. */
  activeImageId: string | null;
  selectedTextId: string | null;
  editingTextId: string | null;
  saving: boolean;
  /** Blocks currently being made editable (strip in flight). */
  convertingIds: string[];
  /** Bumped when fonts finish loading so Konva re-measures text. */
  fontsVersion: number;
  /** Set by the ACTIVE node overlay; used for the full-res export. */
  stage: Konva.Stage | null;

  open: (imageId: string, sourceUrl: string, tileW?: number, tileH?: number) => void;
  /** Deactivate the editor (panel closed / Done) — background prepares keep running. */
  close: () => void;
  /** Drop a failed prepare entry so the tile returns to normal. */
  dismiss: (imageId: string) => void;
  setStage: (stage: Konva.Stage | null) => void;
  setSelected: (id: string | null) => void;
  setEditingText: (id: string | null) => void;
  updateText: (id: string, patch: Partial<EditTextNode>) => void;
  /** v5.90 — make one block editable: strip its region + fit its layer.
   *  Returns an error string or null. */
  convertBlock: (textId: string) => Promise<string | null>;
  addText: () => void;
  deleteText: (id: string) => void;
  /** Export the stage at source resolution → Bunny. For ORIGINAL tiles a
   *  protected copy lands beside the source ('copy'); for already-edited
   *  copies the tile updates IN PLACE ('updated') — no new tiles per save
   *  (Dan: "it should just edit the existing one"). Errors return a string. */
  saveToCanvas: () => Promise<'copy' | 'updated' | string>;
}

let nextRunId = 1;
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced PATCH of the ACTIVE doc (only the active doc receives edits). */
function scheduleAutosave(): void {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => { autosaveTimer = null; flushAutosave(); }, 1200);
}
function flushAutosave(): void {
  if (autosaveTimer) { clearTimeout(autosaveTimer); autosaveTimer = null; }
  const s = useCreativeEditStore.getState();
  const p = s.activeImageId ? s.prepares[s.activeImageId] : null;
  if (p?.docId && p.doc) patchDoc(p.docId, p.doc);
}

function setPrepare(imageId: string, patch: Partial<PrepareState>): void {
  useCreativeEditStore.setState((s) => {
    const cur = s.prepares[imageId];
    if (!cur) return s;
    return { prepares: { ...s.prepares, [imageId]: { ...cur, ...patch } } };
  });
}

/** True while `runId` still owns this tile's prepare entry. */
function owns(imageId: string, runId: number): boolean {
  return useCreativeEditStore.getState().prepares[imageId]?.runId === runId;
}

function startPrepare(imageId: string, sourceUrl: string, tileW?: number, tileH?: number): void {
  const runId = nextRunId++;
  useCreativeEditStore.setState((s) => ({
    prepares: {
      ...s.prepares,
      [imageId]: { runId, sourceUrl, tileW, tileH, docId: null, phase: 'preparing', phaseNote: 'Reading the creative’s text…', error: null, doc: null },
    },
  }));
  void (async () => {
    try {
      const { useCanvasStore } = await import('./canvas-store');
      const projectId = useCanvasStore.getState().activeProjectId;
      const res = await fetch('/api/creative-edit/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: sourceUrl, ...(projectId != null ? { projectId } : {}) }),
      });
      if (res.status === 402) throw new Error('Not enough credits for this edit.');
      if (!res.ok && res.status !== 202) throw new Error((await res.text()).slice(0, 200));
      const { docId } = await res.json();
      if (!owns(imageId, runId)) return;
      setPrepare(imageId, { docId });
      for (let i = 0; i < 100; i++) {
        const dRes = await fetch(`/api/creative-edit/docs/${docId}`);
        if (!owns(imageId, runId)) return;
        if (dRes.ok) {
          const d = await dRes.json();
          if (d.status === 'ready' && d.doc) {
            const doc = d.doc as EditDoc;
            // v5.90 lazy strip: no global fit — blocks are fitted one at a
            // time when the user makes them editable (convertBlock). Fonts
            // for already-struck blocks still need loading.
            const custom = await loadCreativeFontFaces(doc);
            await loadFamilies(Array.from(new Set(doc.texts.map((t) => t.fontFamily))).filter(f => !custom.has(f)));
            if (!owns(imageId, runId)) return;
            setPrepare(imageId, { doc, phase: 'ready' });
            useCreativeEditStore.setState((s) => ({ fontsVersion: s.fontsVersion + 1 }));
            return;
          }
          if (d.status === 'failed') throw new Error(d.error || 'Preparing the editor failed.');
        }
        if (i === 4) setPrepare(imageId, { phaseNote: 'Stripping the text off the image…' });
        if (i === 14) setPrepare(imageId, { phaseNote: 'Rebuilding text as editable layers…' });
        await new Promise((r) => setTimeout(r, 2500));
      }
      throw new Error('Timed out preparing the editor.');
    } catch (e) {
      if (!owns(imageId, runId)) return;
      setPrepare(imageId, { error: e instanceof Error ? e.message : 'Preparing the editor failed.', phase: 'failed' });
    }
  })();
}

export const useCreativeEditStore = create<CreativeEditState>((set, get) => ({
  prepares: {},
  activeImageId: null,
  selectedTextId: null,
  editingTextId: null,
  saving: false,
  convertingIds: [],
  fontsVersion: 0,
  stage: null,

  open: (imageId, sourceUrl, tileW, tileH) => {
    // Warm the whole font library immediately — conversions need verified
    // font metrics, and cold Google Fonts loads take seconds.
    void loadFamilies(EDITOR_FONTS);
    const s = get();
    if (s.activeImageId && s.activeImageId !== imageId) flushAutosave();
    set({ activeImageId: imageId, selectedTextId: null, editingTextId: null, saving: false });
    const existing = s.prepares[imageId];
    if (existing && existing.phase !== 'failed') {
      if (tileW && tileH) setPrepare(imageId, { tileW, tileH });
      return; // ready → instant open; preparing → watch it
    }
    startPrepare(imageId, sourceUrl, tileW, tileH);
  },

  close: () => {
    flushAutosave();
    set({ activeImageId: null, selectedTextId: null, editingTextId: null, saving: false, stage: null });
  },

  dismiss: (imageId) => {
    set((st) => {
      const prepares = { ...st.prepares };
      delete prepares[imageId];
      return { prepares, ...(st.activeImageId === imageId ? { activeImageId: null } : {}) };
    });
  },

  setStage: (stage) => set({ stage }),
  setSelected: (selectedTextId) => set({ selectedTextId }),
  setEditingText: (editingTextId) => set({ editingTextId }),

  updateText: (id, patch) => {
    set((st) => {
      const aid = st.activeImageId;
      const p = aid ? st.prepares[aid] : null;
      if (!aid || !p?.doc) return st;
      return { prepares: { ...st.prepares, [aid]: { ...p, doc: { ...p.doc, texts: p.doc.texts.map((t) => (t.id === id ? { ...t, ...patch } : t)) } } } };
    });
    scheduleAutosave();
  },

  addText: () => {
    const st = get();
    const aid = st.activeImageId;
    const p = aid ? st.prepares[aid] : null;
    if (!aid || !p?.doc) return;
    const doc = p.doc;
    const id = `n${Date.now()}`;
    const fontSize = Math.max(18, Math.round(doc.width * 0.05));
    const node: EditTextNode = {
      id, text: 'New text',
      x: Math.round(doc.width * 0.1), y: Math.round(doc.height * 0.1),
      w: Math.round(doc.width * 0.8), h: Math.round(fontSize * 1.2),
      fontFamily: 'Inter', fontSize, fill: '#ffffff',
      fontStyle: 'bold', align: 'center', lineHeight: 1.1,
    };
    ensureGoogleFont('Inter');
    set({ prepares: { ...st.prepares, [aid]: { ...p, doc: { ...doc, texts: [...doc.texts, node] } } }, selectedTextId: id });
    scheduleAutosave();
  },

  deleteText: (id) => {
    set((st) => {
      const aid = st.activeImageId;
      const p = aid ? st.prepares[aid] : null;
      if (!aid || !p?.doc) return st;
      return {
        prepares: { ...st.prepares, [aid]: { ...p, doc: { ...p.doc, texts: p.doc.texts.filter((t) => t.id !== id) } } },
        selectedTextId: null, editingTextId: null,
      };
    });
    scheduleAutosave();
  },

  convertBlock: async (textId) => {
    const st = get();
    const aid = st.activeImageId;
    const p = aid ? st.prepares[aid] : null;
    if (!aid || !p?.doc || !p.docId) return 'Editor not ready.';
    const t = p.doc.texts.find((x) => x.id === textId);
    if (!t) return 'Text block not found.';
    if (t.struck) return null;
    if (st.convertingIds.includes(textId)) return null;
    set((s) => ({ convertingIds: [...s.convertingIds, textId] }));
    const preStripPlate = p.doc.plateUrl;
    try {
      // Verify and match real fonts before paying to erase the source pixels.
      const fitted = await fitSingleBlock(t, preStripPlate, p.doc.width, p.doc.height, p.doc);
      const res = await fetch(`/api/creative-edit/docs/${p.docId}/strip-block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ textId }),
      });
      if (res.status === 402) throw new Error('Not enough credits.');
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'Strip failed.');
      const { plateUrl } = await res.json();
      set((s) => {
        const cur = s.prepares[aid];
        if (!cur?.doc || cur.runId !== p.runId || cur.docId !== p.docId) return s;
        return {
          prepares: {
            ...s.prepares,
            [aid]: {
              ...cur,
              doc: {
                ...cur.doc,
                plateUrl,
                texts: cur.doc.texts.map((x) => (x.id === textId ? { ...fitted, struck: true } : x)),
              },
            },
          },
          ...(s.activeImageId === aid ? { selectedTextId: textId } : {}),
          fontsVersion: s.fontsVersion + 1,
        };
      });
      const converted = get().prepares[aid];
      if (converted?.runId === p.runId && converted.docId === p.docId && converted.doc) {
        patchDoc(p.docId, converted.doc);
      }
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Strip failed.';
    } finally {
      set((s) => ({ convertingIds: s.convertingIds.filter((x) => x !== textId) }));
    }
  },

  saveToCanvas: async () => {
    const st = get();
    const aid = st.activeImageId;
    const p = aid ? st.prepares[aid] : null;
    const { stage, saving } = st;
    if (!aid || !p?.doc || !stage || saving) return 'Editor not ready.';
    const doc = p.doc;
    set({ saving: true, selectedTextId: null, editingTextId: null });
    try {
      // Plain timeout + synchronous draw — requestAnimationFrame never
      // fires in a hidden/backgrounded tab and hung the save forever.
      await new Promise((r) => setTimeout(r, 80));
      // Selection chrome must never print: React effects defer in hidden
      // tabs, so the Transformer detach can lag the state change — detach
      // imperatively before capture.
      try {
        (stage.find('Transformer') as unknown as Array<{ nodes: (n: unknown[]) => void }>).forEach((tr) => tr.nodes([]));
      } catch { /* non-fatal */ }
      // v5.90 — never export before the plate bitmap is loaded (the judge
      // suite caught a save racing the plate load → blank white PNG).
      for (let i = 0; i < 30; i++) {
        const imgNode = stage.findOne('Image') as Konva.Image | undefined;
        const el = imgNode?.image() as HTMLImageElement | undefined;
        if (el && el.complete && el.naturalWidth > 0) break;
        if (i === 29) { set({ saving: false }); return 'Image still loading — try Save again in a moment.'; }
        await new Promise((r) => setTimeout(r, 200));
      }
      stage.draw();
      const scale = stage.scaleX() || 1;
      // v5.83 — capture the DOC's exact region (not the stage box) so the
      // export is precisely doc.width × doc.height.
      // v7.55 — render to a canvas and ship the PNG as BINARY multipart. The
      // old base64 data URL in JSON was 33% bigger than the image and, at the
      // 4K default (3840² ≈ 30 MB PNG), tripped the server's 12 MB cap on
      // every save — "Save failed" with nothing the user could do about it.
      const exportCanvas = stage.toCanvas({
        x: 0, y: 0,
        width: doc.width * scale, height: doc.height * scale,
        pixelRatio: 1 / scale,
      });
      const blob = await new Promise<Blob | null>((r) => exportCanvas.toBlob(r, 'image/png'));
      if (!blob) throw new Error('Could not render the image.');
      const form = new FormData();
      form.append('image', blob, 'export.png');
      // v5.88 — the copy registers as a ready edit doc (same plate +
      // layers): Edit on the copy opens instantly, never re-scans.
      form.append('doc', JSON.stringify(doc));
      // No Content-Type header: the browser sets the multipart boundary.
      const res = await fetch('/api/creative-edit/export', { method: 'POST', body: form });
      if (!res.ok) {
        let msg = `Save failed (${res.status}).`;
        try {
          const j = (await res.json()) as { error?: string; message?: string };
          msg = j.error || j.message || msg;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const { url, docId: newDocId } = await res.json();
      const cs = (await import('./canvas-store')).useCanvasStore.getState();
      // v5.92 — a tile that IS an edited copy updates IN PLACE; only
      // originals spawn a protected copy beside them. Duplicating is the
      // explicit "new version" action.
      const isEditedCopy = (p.sourceUrl ?? '').includes('/creative-edit/exports/');
      if (isEditedCopy) {
        cs.updateImage(aid, { src: url });
        set((s2) => {
          const cur = s2.prepares[aid];
          return cur ? {
            saving: false,
            prepares: { ...s2.prepares, [aid]: { ...cur, sourceUrl: url, docId: typeof newDocId === 'number' ? newDocId : cur.docId } },
          } : { saving: false };
        });
        return 'updated';
      }
      // v5.83 — the new tile takes the SOURCE tile's display size (captured
      // at open()) so it sits beside the original at the identical visual
      // size/ratio (doc pixel dims made every edit a 320×320 square).
      cs.addImageNextTo(
        url,
        p.tileW ?? doc.width,
        p.tileH ?? doc.height,
        `edited-${Date.now()}.png`,
        aid,
        doc.width,
        doc.height,
      );
      set({ saving: false });
      return 'copy';
    } catch (e) {
      set({ saving: false });
      return e instanceof Error ? e.message : 'Export failed';
    }
  },
}));
